import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import LexTabs from '_lex/_atoms/Tabs';
import { translate as t } from 'hooks/useTranslations';
import { useStandardsContext } from '../context';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import EditorSheet from '_lex/_atoms/EditorSheet';
import { neutral } from '_lex/_foundations/phxColors';
import { ReactComponent as NoStandardSelected } from '../../images/no_standard_selected.svg';
import StandardsList from '../List';
import StandardActions from '../_components/StandardActions';
import WrittenDirectivesSummary from './WrittenDirectivesSummary';
import StandardTabContent, { type StandardMode, type StandardTabContentRef } from './StandardTabContent';
import ComplianceCriteriaTabContent from './ComplianceCriteriaTabContent';
import { canDeleteLexipolComplianceItems } from '../../../../_helpers/awbHelper';
import { Jurisdiction } from 'types/jurisdiction';
import { AppContext } from 'views/App/context';

const TAB_STANDARD = 'standard';
const TAB_COMPLIANCE_CRITERIA = 'compliance-criteria';

const StandardDetail: React.FC = () => {
  const { id: accreditationId, versionId, standardId } = useParams();
  const { version, getStandardType, versionUpdateCounter } = useStandardsContext();
  const { isFeatureEnabled } = useContext(AppContext);
  const hasComplianceTab = isFeatureEnabled('awbPremiumLexipol');
  const allowDeleteComplianceItems = canDeleteLexipolComplianceItems(version);

  const [contentTab, setContentTab] = useState<string>(TAB_STANDARD);
  const [standardTypeText, setStandardTypeText] = useState('');
  const [standardTabMode, setStandardTabMode] = useState<StandardMode>('EDITABLE');
  const [standardTabSaving, setStandardTabSaving] = useState(false);

  const standardTabRef = useRef<StandardTabContentRef>(null);

  const handleModeChange = useCallback((mode: StandardMode) => {
    setStandardTabMode(mode);
  }, []);

  useEffect(() => {
    if (!standardId || !getStandardType) return;
    const type = getStandardType(standardId);
    switch (type) {
      case 'standard':
        setStandardTypeText(t('awb.standards.new.standard'));
        break;
      case 'subStandard':
        setStandardTypeText(t('awb.standards.new.subStandard'));
        break;
      case 'requirement':
        setStandardTypeText(t('awb.standards.new.requirement'));
        break;
      default:
        setStandardTypeText(t('awb.standards.new.standard'));
    }
  }, [standardId, getStandardType, versionUpdateCounter, contentTab]);

  const renderContent = () => {
    if (!standardId) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '60vh',
          }}
        >
          <NoStandardSelected style={{ height: '240px' }} />
          <Typography sx={{ color: neutral[500] }}>{t('awb.standards.emptyScreen')}</Typography>
        </Box>
      );
    }

    return (
      <EditorSheet sx={{ gridTemplateRows: 'auto 1fr' }}>
        <Box>
          <LexTabs
            value={contentTab}
            onChange={(_, v: string | number) => setContentTab(v as string)}
            items={[
              { label: standardTypeText || t('awb.standards.new.standard'), value: TAB_STANDARD },
              ...(hasComplianceTab
            ? [{ label: t('awb.standards.detail.complianceCriteriaTab'), value: TAB_COMPLIANCE_CRITERIA }]
            : []),
            ]}
          />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 5, py: 3 }}>
          {contentTab === TAB_STANDARD && (
            <StandardTabContent
              ref={standardTabRef}
              standardId={standardId}
              onModeChange={handleModeChange}
              onSavingChange={setStandardTabSaving}
            />
          )}
          {hasComplianceTab && contentTab === TAB_COMPLIANCE_CRITERIA && (
            <ComplianceCriteriaTabContent
              standardId={standardId}
              allowDeleteComplianceItems={allowDeleteComplianceItems}
            />
          )}
        </Box>
      </EditorSheet>
    );
  };

  return (
    <TakeoverLayout
      pageTitle={version?.version || ''}
      backUrl={`/admin/awb/accrediting-bodies/${accreditationId}/versions/`}
      menubarLeft={<StandardActions />}
      leftDrawer={<StandardsList />}
      contentBackground='neutral'
      rightDrawer={
        standardId &&
        version && (
          <WrittenDirectivesSummary
            standardId={standardId}
            jurisdictions={
              Array.isArray(version?.jurisdictions) && typeof version?.jurisdictions[0] === 'object'
                ? (version.jurisdictions as Jurisdiction[])
                : []
            }
          />
        )
      }
      contentHeaderRight={
        standardId &&
        (!hasComplianceTab || contentTab === TAB_STANDARD) &&
        (standardTabMode === 'EDITING' ? (
          <Stack
            direction='row'
            spacing={1}
          >
            <Button
              variant='outlined'
              color='neutral'
              size='small'
              onClick={() => standardTabRef.current?.cancelEditing()}
              disabled={standardTabSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={() => standardTabRef.current?.save()}
              disabled={standardTabSaving}
              loading={standardTabSaving}
              loadingPosition='end'
            >
              {t('awb.standards.detail.save')}
            </Button>
          </Stack>
        ) : (
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={() => standardTabRef.current?.startEditing()}
          >
            {t('awb.standards.detail.edit')}
          </Button>
        ))
      }
    >
      {renderContent()}
    </TakeoverLayout>
  );
};

export default StandardDetail;
